import type { VoidFunctionComponent } from 'react';
import type { Simplify } from 'type-fest';

import Icon from './Icon/Icon';
import Tooltip from './Tooltip';
import type { IconProps } from './Icon/Icon';
import type { TooltipProps } from './Tooltip';

type Props = Simplify<TooltipProps & IconProps>;

/**
 * Have you ever wanted to show an Icon with a tooltip, but in fewer lines than doing it by hand? Look no further!
 *
 * The TooltipIcon component easily lets you display an icon with a tooltip in a single generic instead of with two.
 *
 * @param {Props} props - props object
 * @param {string} props.id - Unique id for the tooltip. This helps with a11y.
 * @param {React.ReactNode} props.children - A single element or component to be "attached" to. It should have `display: inline-block`.
 * @param {string | React.ReactNode} props.tooltip - Tooltip text. Can also be a ReactNode.
 * @param {'top' | 'bottom' | 'right' | 'left'} [props.placement] - Placement of the tooltip.
 * @param {boolean} [props.showTooltip] - If the tooltip should be displayed or not (children rendered as fallback).
 * @param {IconClassName} props.icon The icon which you wish to use (see link above).
 * @param {boolean} [props.hasSpin] If the fa-spin class should be added to the icon.
 * @param {string} [props.size] Currently adds the fa-lg class to the icon when it equals to 'lg'. Other sizes and `fossa-` icon support may be added.
 * @example
 *  <TooltipIcon
 *    tooltip={`Ignores the selected issues in this ${projectOrReleaseGroup}.`}
 *    icon={icons.circleQuestion}
 *    id="my-tooltip-icon"
 *   />
 */
const TooltipIcon: VoidFunctionComponent<Props> = ({
  id,
  tooltip,
  placement,
  showTooltip,
  icon,
  hasSpin,
  size,
  testId,
}) => (
  <Tooltip id={id} tooltip={tooltip} placement={placement} showTooltip={showTooltip}>
    <span>
      <Icon icon={icon} hasSpin={hasSpin} size={size} testId={testId} />
    </span>
  </Tooltip>
);

export default TooltipIcon;
